/**
 * ActivityTimeline — feed unificado de mensajes, voz, ubicaciones, misiones.
 */
import React, { useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import { Mic, MessageSquare, MapPin, Target, AlertTriangle, Image as ImageIcon, Play, Pause } from 'lucide-react';
import Header from './Header';
import useStore from '../store/useStore';

const KIND_STYLE = {
  voice:   { hue: '#007AFF', tint: '#E6F1FF', icon: Mic },
  text:    { hue: '#48484A', tint: '#F5F5F7', icon: MessageSquare },
  image:   { hue: '#5856D6', tint: '#ECECFB', icon: ImageIcon },
  sos:     { hue: '#FF2D55', tint: '#FFE5EC', icon: AlertTriangle },
  event:   { hue: '#34C759', tint: '#E5F9EC', icon: MapPin },
  mission: { hue: '#FF9F0A', tint: '#FFF3DE', icon: Target },
};

function fmtTime(ts) {
  const d = new Date(ts);
  return d.toLocaleString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function VoiceItem({ item }) {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const toggle = async () => {
    if (!item.audioFile) return;
    if (!audioRef.current) {
      audioRef.current = new Audio(item.audioFile);
      audioRef.current.onended = () => setPlaying(false);
    }
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { try { await audioRef.current.play(); setPlaying(true); } catch (_) {} }
  };
  return (
    <div className="flex items-start gap-2">
      {item.audioFile ? (
        <button onClick={toggle} className="px-3 py-1.5 rounded-full text-[11px] font-bold flex items-center gap-1 shadow-apple-sm flex-shrink-0"
          style={{ background: playing ? '#007AFF' : '#E6F1FF', color: playing ? '#fff' : '#007AFF' }}>
          {playing ? <Pause size={12} /> : <Play size={12} />}{Math.round(item.duration || 0)}s
        </button>
      ) : (
        <span className="text-[11px] text-n-500 px-2 py-1.5">{Math.round(item.duration || 0)}s</span>
      )}
      <div className="flex-1 min-w-0">
        {item.transcription ? (
          <p className="text-[12.5px] text-n-800 leading-snug">{item.transcription}</p>
        ) : (
          <p className="text-[11px] text-n-400 italic">Audio sin transcribir</p>
        )}
      </div>
    </div>
  );
}

function Item({ item }) {
  const style = KIND_STYLE[item.kind] || KIND_STYLE.text;
  const Icon = style.icon;
  return (
    <div className="flex gap-3 mb-3 animate-fade-in">
      <div className="flex flex-col items-center flex-shrink-0">
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center text-white shadow-apple-sm"
          style={{ background: style.hue }}
        >
          <Icon size={14} />
        </div>
        <div className="flex-1 w-0.5 mt-1" style={{ background: style.tint }} />
      </div>
      <div className="flex-1 min-w-0 rounded-md p-3 shadow-apple-sm" style={{ background: style.tint, border: `1px solid ${style.hue}22` }}>
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-2">
            {item.username && (
              <div
                className="w-5 h-5 rounded-full text-[9px] font-bold text-white flex items-center justify-center"
                style={{ background: item.avatarColor || style.hue }}
              >
                {item.username[0]?.toUpperCase()}
              </div>
            )}
            <span className="text-[11px] font-semibold text-n-700">{item.username || '—'}</span>
            {item.channelName && <span className="text-[10px] text-n-500">#{item.channelName}</span>}
          </div>
          <span className="text-[10px] text-n-500 font-mono">{fmtTime(item.ts)}</span>
        </div>

        {item.kind === 'voice' && <VoiceItem item={item} />}
        {item.kind === 'text' && <p className="text-[12.5px] text-n-800 whitespace-pre-wrap">{item.content}</p>}
        {item.kind === 'image' && (
          <a href={item.content} target="_blank" rel="noreferrer">
            <img src={item.content} alt="adjunto" className="max-w-[220px] rounded-sm" />
          </a>
        )}
        {item.kind === 'sos' && (
          <p className="text-[12.5px] font-bold" style={{ color: style.hue }}>
            🚨 SOS{item.lat ? ` · ${item.lat.toFixed(4)}, ${item.lng.toFixed(4)}` : ''}
          </p>
        )}
        {item.kind === 'event' && (
          <p className="text-[12.5px] text-n-800">
            {item.eventType}{item.lat ? ` · ${item.lat.toFixed(4)}, ${item.lng.toFixed(4)}` : ''}
          </p>
        )}
        {item.kind === 'mission' && (
          <p className="text-[12.5px] text-n-800">
            <strong>{item.title}</strong>
            <span className="ml-2 text-[10px] uppercase font-bold" style={{ color: style.hue }}>
              {item.priority} · {item.status}
            </span>
          </p>
        )}
      </div>
    </div>
  );
}

export default function ActivityTimeline() {
  const channels = useStore(s => s.channels);
  const user = useStore(s => s.user);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState('all'); // 'all' | 'me'
  const [channelFilter, setChannelFilter] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', '120');
      if (scope === 'me' && user?.id) params.set('userId', user.id);
      if (channelFilter) params.set('channelId', channelFilter);
      const r = await axios.get('/api/activity?' + params.toString());
      setItems(r.data || []);
    } catch (e) { console.warn('[Activity] load', e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope, channelFilter]);

  return (
    <div className="flex flex-col h-screen overflow-hidden" style={{ background: '#FBFBFD' }}>
      <Header />
      <div className="flex-1 overflow-y-auto p-4 md:p-6 max-w-3xl mx-auto w-full">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-extrabold text-n-800 tracking-apple-tighter">Actividad</h1>
            <p className="text-xs text-n-500">Feed unificado · últimos 7 días</p>
          </div>
          <div className="flex items-center gap-2">
            <select value={scope} onChange={(e) => setScope(e.target.value)}
              className="text-xs px-3 py-1.5 rounded-full border border-n-200 bg-white">
              <option value="all">Toda la org</option>
              <option value="me">Solo yo</option>
            </select>
            <select value={channelFilter} onChange={(e) => setChannelFilter(e.target.value)}
              className="text-xs px-3 py-1.5 rounded-full border border-n-200 bg-white">
              <option value="">Todos los canales</option>
              {(channels || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-n-500">Cargando…</p>
        ) : items.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-sm text-n-400 italic">Sin actividad en este rango.</p>
          </div>
        ) : (
          <div>{items.map(it => <Item key={it.id} item={it} />)}</div>
        )}
      </div>
    </div>
  );
}
